"use client";

import { useState, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { storyblokEditable, StoryblokComponent } from "@storyblok/react/rsc";

import { EventTag } from "@/components/layout/EventTag";
import { ArrowRightThin } from "@/components/icons";
import { ArrowRight } from "@/components/icons/ArrowRight";
import { Button } from "@/components/ui/button";
import { expandToOccurrences, formatDateParam } from "@/lib/utils/recurrence";
import { formatEventDateRange } from "@/lib/utils/date";
import type { InstagramLinktreeStoryblok, EventStoryblok } from "@/component-types-sb";
import { eventTypeFromContent } from "@/lib/utils/eventType";
import { fetchEventsAndTravels } from "@/lib/storyblok/fetchList";

type InstagramLinktreeProps = {
  blok: InstagramLinktreeStoryblok;
};

type NextEventData = {
  title: string;
  date: Date;
  endDate?: Date;
  eventType: string;
  memberOnly: boolean;
  ctaLink: string;
};

const InstagramLinktree = ({ blok }: InstagramLinktreeProps) => {
  const [nextEvent, setNextEvent] = useState<NextEventData | null>(null);

  useEffect(() => {
    const fetchNextEvent = async () => {
      try {
        // Der naechste Termin ueberhaupt — Events und Reisen gleichberechtigt.
        const stories = await fetchEventsAndTravels({
          sort_by: "content.date:asc",
          per_page: 100,
        });

        if (stories?.length > 0) {
          const occurrences = stories.flatMap(expandToOccurrences);
          occurrences.sort(
            (a: any, b: any) => a.displayDate.getTime() - b.displayDate.getTime(),
          );

          if (occurrences.length > 0) {
            const occ = occurrences[0];
            const content = occ.story.content as EventStoryblok;
            setNextEvent({
              title: content.title,
              date: occ.displayDate,
              endDate: occ.endDate,
              eventType: eventTypeFromContent(content),
              memberOnly: (content as { member_access?: string }).member_access === "member_only",
              ctaLink: occ.isRepeating
                ? `/${occ.story.full_slug}?date=${formatDateParam(occ.displayDate)}`
                : `/${occ.story.full_slug}`,
            });
          }
        }
      } catch (error) {
        console.error("Error fetching next event:", error);
      }
    };

    fetchNextEvent();
  }, []);

  const formattedDate = nextEvent
    ? nextEvent.endDate
      ? formatEventDateRange(nextEvent.date, nextEvent.endDate)
      : format(nextEvent.date, "EEEE, dd. MMM yyyy", { locale: de })
    : "";

  return (
    <div
      className="min-h-screen bg-sand-10 flex flex-col items-center"
      {...storyblokEditable(blok)}
    >
      <div className="w-full max-w-lg lg:max-w-screen-sm mx-auto py-6 flex flex-col gap-4">
        {/* Profile header */}
        <header className="flex items-center gap-3 px-4">
          {blok.profile_image?.filename && (
            <Image
              src={blok.profile_image.filename}
              alt={blok.profile_image.alt || blok.profile_name || ""}
              width={40}
              height={40}
              className="rounded-full object-cover size-10"
            />
          )}
          {blok.profile_name && (
            <span className="text-headline-md text-black">
              {blok.profile_name}
            </span>
          )}
        </header>

        {/* Next event */}
        {nextEvent && (
          <div className="flex flex-col gap-4">
            {blok.event_headline && (
              <p className="text-3xl font-normal text-center text-black">
                {blok.event_headline}
              </p>
            )}
            <Link
              href={nextEvent.ctaLink}
              className="bg-white rounded-2xl p-3 flex items-center gap-4"
            >
              <div className="flex flex-col items-start flex-1 min-w-0">
                {nextEvent.eventType && (
                  <div className="mb-2">
                    <EventTag variant={nextEvent.eventType} memberOnly={nextEvent.memberOnly} />
                  </div>
                )}
                <p className="text-base text-gray-100">{formattedDate}</p>
                <p className="text-2xl font-bold text-black">{nextEvent.title}</p>
              </div>
              <ArrowRightThin className="shrink-0" />
            </Link>
          </div>
        )}

        {/* Body sections (newsletter, etc.) */}
        <div className="linktree-body">
          {blok.body?.map((nestedBlok) => (
            <StoryblokComponent blok={nestedBlok} key={nestedBlok._uid} />
          ))}
        </div>

        {/* Membership CTA */}
        <Link href="/membership">
          <Button variant="black" className="w-full">
            Jetzt Mitglied werden
            <ArrowRight />
          </Button>
        </Link>

        {/* Grid headline */}
        {blok.grid_headline && (
          <p className="text-copy text-center text-black">
            {blok.grid_headline}
          </p>
        )}

        {/* Instagram image grid */}
        {blok.items && blok.items.length > 0 && (
          <div className="grid grid-cols-3 gap-1">
            {blok.items.map((item) => (
              <StoryblokComponent blok={item} key={item._uid} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default InstagramLinktree;
